import { CancelToken } from 'axios';
import {
    IotaExtension,
    InputBinding,
    ApplicationDefinitionSaveSuccessModel,
    ApiOperationInfo,
    ApiApplicationCreateModel,
    InternalSelectOption,
    ApplicationFieldQueryDefinition
} from '../types';
import { ApplicationDefinitionsResult } from '../types';

import { ApplicationDefinitionBase, InputFieldSettingDto } from '../types/ApplicationDefinition';
import appClient from '../../common/services/AppClient';
import { ResultApi } from '../../common/services/AppClient';
import security from '../../common/services/SecurityService';
import { Utils } from '../../common/services/Utils';
import { ApplicationDefinitionPreviewBindingResponse } from '../types/ApplicationDefinitionPreviewBindingModel';
import ApplicationDefinitionFactory from '../misc/ApplicationDefinitionFactory';
import { RcFile } from 'antd/lib/upload';
import { endpoints } from '../../../config';

export default class ApplicationDefinitionsService {
    async getAllApplicationDefinitions(): Promise<ApplicationDefinitionBase[]> {
        throw new Error(
            'Global application definition reads are not supported by ManageService. Provide a project id.'
        );
    }

    async getApplicationDefinitionsForProject(projectId: string): Promise<ApplicationDefinitionBase[]> {
        const result = await appClient.get<ApplicationDefinitionsResult['getAppDefinitions']>(
            `${endpoints.manageUrl}projects/${projectId}/iota/applications`
        );
        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        return result.value.map(ApplicationDefinitionFactory.createApplicationDefinition) || [];
    }

    async getApplicationDefinition(projectId: string, id: string): Promise<ApplicationDefinitionBase | null> {
        const result = await appClient.get<ApplicationDefinitionsResult['getAppDefinitions'][number] | null>(
            `${endpoints.manageUrl}projects/${projectId}/iota/applications/${id}`
        );
        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        if (!result.value) {
            return null;
        }

        return ApplicationDefinitionFactory.createApplicationDefinition(result.value);
    }

    async getApplicationQueryDefinitions(
        projectId: string,
        applicationDefinitionId: string
    ): Promise<ApplicationFieldQueryDefinition[]> {
        const encodedProjectId = encodeURIComponent(projectId);
        const encodedApplicationDefinitionId = encodeURIComponent(applicationDefinitionId);
        const result = await appClient.get<ApplicationFieldQueryDefinition[]>(
            `${endpoints.manageUrl}projects/${encodedProjectId}/iota/applications/${encodedApplicationDefinitionId}/query-definitions`
        );

        if (result.isErr()) {
            console.log(result.error);
            throw result.error;
        }

        return result.value;
    }

    async getMetadata(): Promise<IotaExtension[]> {
        const url = endpoints.iotaUrl + 'metadata';
        const resp = await appClient.get<IotaExtension[]>(url);
        return resp.unwrapOr(Array<IotaExtension>());
    }

    async syncMetadata(projectId: string): Promise<void> {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/metadata/sync`;
        await appClient.post(url);
    }

    async syncSettings(projectId: string): Promise<void> {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/sync-settings`;
        await appClient.post(url);
    }

    createApplicationDefinition(
        formData: FormData,
        projectId: string
    ): Promise<ResultApi<ApplicationDefinitionSaveSuccessModel>> {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications`;
        return appClient.post<ApplicationDefinitionSaveSuccessModel>(url, formData);
    }

    async deleteApplicationDefinition(appDefId: string, projectId: string): Promise<ResultApi<unknown>> {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}`;
        return appClient.delete(url);
    }

    async editApplicationDefinition(
        formData: FormData,
        appDefId: string,
        projectId: string
    ): Promise<ResultApi<ApplicationDefinitionSaveSuccessModel>> {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}`;
        return appClient.update<ApplicationDefinitionSaveSuccessModel>(url, formData);
    }

    async updateBindings(
        projectId: string,
        appDefId: string,
        bindings: InputBinding[],
        fieldSettings: InputFieldSettingDto[]
    ) {
        const request = {
            bindings: bindings,
            fieldSettings
        };

        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/inputs`;
        return appClient.post(url, request);
    }

    async updateApplicationSettings(
        appDefId: string,
        settings: { [id: string]: unknown },
        projectId: string
    ): Promise<ResultApi<unknown>> {
        const request = {
            settingValues: settings
        };

        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/settings`;
        return appClient.update(url, request);
    }

    async updateApplicationState(appDefId: string, state: string, projectId: string): Promise<ResultApi<unknown>> {
        const request = {
            applicationId: appDefId,
            state: state
        };

        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/state`;
        return appClient.update(url, request);
    }

    async getApplicationIcon(appDefId: string, projectId: string): Promise<Blob | undefined> {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/icon`;
        const resp = await appClient.get<Blob | undefined>(url, 'blob');
        return resp.unwrapOr(undefined);
    }

    async applicationDefinitionPreviewBinding(
        projectId: string,
        appDefId: string,
        inputBindings: InputBinding[],
        packageIds: string[]
    ): Promise<ResultApi<Array<ApplicationDefinitionPreviewBindingResponse>>> {
        const request = {
            inputs: inputBindings,
            packageIds
        };

        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/${appDefId}/preview-binding`;
        return await appClient.post(url, request);
    }

    async exportApp(id: string, projectId: string) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'iotaPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';
        const mapIotaIdInput = document.createElement('input');
        mapIotaIdInput.type = 'text';
        mapIotaIdInput.name = 'id';
        mapIotaIdInput.value = id;
        mapForm.appendChild(mapIotaIdInput);

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/export${t}`;
            Utils.downloadFile(url, mapForm, 'iotaPostForm');
            return Promise.resolve();
        });
    }

    async deleteApplicationDefinitionGroup(projectId: string, id: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/${id}/applications/group`;
        return appClient.delete(url);
    }

    async moveApplicationDefinition(projectId: string, appDefid: string, groupId: string) {
        const request = {
            appDefid: appDefid,
            groupId: groupId
        };
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/movedefinition`;
        return appClient.post(url, request);
    }

    async getExternalApiOperations(apiUrl: string) {
        const encodedUrl = encodeURIComponent(apiUrl);
        const url = endpoints.iotaUrl + `external-api-clients?url=${encodedUrl}`;
        const resp = await appClient.get<ApiOperationInfo[]>(url);
        return resp.unwrapOr([]);
    }

    async createApiApplicationDefinition(
        model: ApiApplicationCreateModel,
        projectId: string
    ): Promise<ResultApi<ApplicationDefinitionSaveSuccessModel>> {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/api-applications`;
        return appClient.post<ApplicationDefinitionSaveSuccessModel>(url, model);
    }

    getAuthToken() {
        return security.invoke(token => {
            return Promise.resolve(token);
        });
    }

    async exportAppBindings(id: string, projectId: string) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'iotaBindingsPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';
        const mapIotaIdInput = document.createElement('input');
        mapIotaIdInput.type = 'text';
        mapIotaIdInput.name = 'id';
        mapIotaIdInput.value = id;
        mapForm.appendChild(mapIotaIdInput);

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/${id}/export-bindings${t}`;
            Utils.downloadFile(url, mapForm, 'iotaBindingsPostForm');
            return Promise.resolve();
        });
    }

    async commitAppBindings(id: string, projectId: string, file: RcFile) {
        var formData = new FormData();
        formData.append('file', file);
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/${id}/commit-bindings`;
        return appClient.post(url, formData);
    }

    async validateConnectionAndSchema(projectId: string, connectionString: string, tableSchema: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/validate-connection-and-schema`;
        return appClient.post(url, { connectionString, tableSchema });
    }

    async getInternalSelectOptions(projectId: string, cancelToken?: CancelToken) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/internal-select-options`;
        return appClient.get<Record<string, InternalSelectOption[]>>(url, 'json', cancelToken);
    }
}
